import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Calendar,
  Check,
  ChevronLeft,
  FolderKanban,
  Home,
  Pipette,
  RotateCcw,
  Settings,
} from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  cn,
} from '@clarix/ui';
import { PickerPanel } from '@clarix/ui-shared';
import { SIDEBAR_PRESETS } from './theme/sidebarPresets';
import { deriveSidebarTheme, isDarkColor } from './theme/sidebarTheme';
import { useSidebarTheme, type SidebarTheme } from './hooks/useSidebarTheme';

/**
 * "Customize navigation" dialog — exact port of clario-frontend's
 * SidebarThemePanel, with Redux userPreferences swapped for the
 * localStorage-backed useSidebarTheme store (preferences endpoint pending).
 */

// ── Mini Rail Preview ─────────────────────────────────────────────────

function MiniRailPreview({ railColor }: { railColor: string }) {
  const derived = deriveSidebarTheme(railColor);
  const isDark = isDarkColor(railColor);

  const mockIcons = [
    { Icon: Home, active: true },
    { Icon: FolderKanban, active: false },
    { Icon: Calendar, active: false },
    { Icon: Settings, active: false },
  ];

  return (
    <div
      className="overflow-hidden rounded-xl border shadow-sm"
      style={{ borderColor: derived.borderColor }}
    >
      <div className="flex h-6 items-center gap-1 border-b border-gray-100 bg-white px-2">
        <div className="h-1.5 w-1.5 rounded-full bg-red-300" />
        <div className="h-1.5 w-1.5 rounded-full bg-yellow-300" />
        <div className="h-1.5 w-1.5 rounded-full bg-green-300" />
        <div className="ml-2 h-1.5 w-14 rounded bg-gray-100" />
      </div>

      <div className="flex" style={{ height: '130px' }}>
        <div
          className="flex flex-col items-center gap-2 pt-2.5"
          style={{
            width: '40px',
            background: derived.railBg,
            borderRight: `1px solid ${derived.borderColor}`,
          }}
        >
          {mockIcons.map(({ Icon, active }, i) => (
            <div
              key={i}
              className="flex items-center justify-center rounded"
              style={{
                width: '26px',
                height: '26px',
                background: active ? derived.activeBg : 'transparent',
                color: derived.iconColor,
                opacity: active ? 1 : isDark ? 0.6 : 0.5,
              }}
            >
              <Icon style={{ width: '13px', height: '13px', strokeWidth: 1.5 }} />
            </div>
          ))}
        </div>

        <div
          className="flex flex-col gap-1.5 border-r border-gray-100 bg-gray-50 p-2"
          style={{ width: '72px' }}
        >
          <div className="h-1.5 w-full rounded bg-gray-200" />
          <div className="h-1.5 w-3/4 rounded bg-gray-100" />
          <div className="h-1.5 w-2/3 rounded bg-gray-100" />
          <div className="mt-1 h-1.5 w-full rounded bg-gray-200" />
          <div className="h-1.5 w-4/5 rounded bg-gray-100" />
        </div>

        <div className="flex flex-1 flex-col gap-1.5 bg-white p-2">
          <div className="h-2 w-3/4 rounded bg-gray-200" />
          <div className="h-1.5 w-full rounded bg-gray-100" />
          <div className="h-1.5 w-2/3 rounded bg-gray-100" />
          <div className="mt-1 flex gap-1">
            <div className="h-4 flex-1 rounded bg-gray-100" />
            <div className="h-4 flex-1 rounded bg-gray-100" />
          </div>
          <div className="h-1.5 w-full rounded bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────

interface SidebarThemePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SidebarThemePanel({ open, onOpenChange }: SidebarThemePanelProps) {
  const { theme: sidebarTheme, setTheme } = useSidebarTheme();
  const [liveColor, setLiveColor] = useState(sidebarTheme.railColor);
  // false = preset palette view, true = custom picker view
  const [showPicker, setShowPicker] = useState(false);

  // Radix DismissableLayer registers a pointerdown listener on document
  // with { capture: true }, which fires BEFORE any bubble-phase handlers.
  // Attach our own capture-phase listener on the picker wrapper that calls
  // stopPropagation() so Radix's document listener never sees the event.
  const pickerWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = pickerWrapperRef.current;
    if (!el) return;
    const stopCapture = (e: PointerEvent) => e.stopPropagation();
    el.addEventListener('pointerdown', stopCapture, { capture: true });
    return () => el.removeEventListener('pointerdown', stopCapture, { capture: true });
  }, [showPicker]);

  // Reset to presets view & sync color only when dialog opens.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setLiveColor(sidebarTheme.railColor);
      setShowPicker(false);
    }
    prevOpenRef.current = open;
  }, [open, sidebarTheme.railColor]);

  const handlePresetClick = useCallback(
    (preset: (typeof SIDEBAR_PRESETS)[number]) => {
      const theme: SidebarTheme = { preset: preset.id, railColor: preset.railColor };
      setLiveColor(preset.railColor);
      setTheme(theme);
    },
    [setTheme],
  );

  const handleCustomColor = useCallback(
    (hex: string) => {
      setLiveColor(hex);
      setTheme({ preset: null, railColor: hex });
    },
    [setTheme],
  );

  const handleReset = useCallback(() => {
    setLiveColor('#FFFFFF');
    setTheme({ preset: 'default', railColor: '#FFFFFF' });
    setShowPicker(false);
  }, [setTheme]);

  const isCustomActive = sidebarTheme.preset === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[620px] p-0">
        <DialogHeader>
          <DialogTitle>Sidebar color</DialogTitle>
        </DialogHeader>

        <DialogBody className="overflow-hidden p-0">
          <div className="flex min-h-0">
            {/* ── Left panel ──────────────────────────────────── */}
            <div className="border-border flex flex-col border-r" style={{ width: '290px' }}>
              {/* VIEW 1 — Preset palette */}
              {!showPicker && (
                <div className="flex flex-1 flex-col px-5 pb-0 pt-4">
                  <p className="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wider">
                    Color themes
                  </p>
                  <div className="grid grid-cols-5 gap-3">
                    {SIDEBAR_PRESETS.map((preset) => {
                      const isSelected = sidebarTheme.preset === preset.id;
                      const dark = isDarkColor(preset.railColor);
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          title={preset.name}
                          onClick={() => handlePresetClick(preset)}
                          className={cn(
                            'flex h-9 w-9 items-center justify-center rounded-full transition-all duration-150',
                            isSelected
                              ? 'scale-110 ring-2 ring-indigo-500 ring-offset-2'
                              : 'ring-1 ring-black/10 hover:scale-110 hover:shadow-md',
                          )}
                          style={{ backgroundColor: preset.railColor }}
                        >
                          {isSelected && (
                            <Check
                              className={cn('h-3.5 w-3.5', dark ? 'text-white' : 'text-gray-700')}
                              strokeWidth={2.5}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Custom color button */}
                  <div className="mt-5">
                    <p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider">
                      Custom color
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowPicker(true)}
                      className={cn(
                        'flex h-9 w-full items-center gap-2.5 rounded-md border px-3 text-xl transition-colors',
                        isCustomActive
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                          : 'border-input bg-background hover:bg-accent/50 text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <div
                        className="h-5 w-5 shrink-0 rounded-md border border-black/10 shadow-sm"
                        style={{ backgroundColor: liveColor }}
                      />
                      <span className="flex-1 text-left font-mono text-lg">
                        {liveColor.toUpperCase()}
                      </span>
                      <Pipette className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              )}

              {/* VIEW 2 — Custom color picker */}
              {showPicker && (
                <div className="flex flex-1 flex-col pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPicker(false)}
                    className="text-muted-foreground hover:text-foreground mb-1 flex w-fit items-center gap-1 px-4 py-1.5 text-lg transition-colors"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Color themes
                  </button>
                  <div ref={pickerWrapperRef}>
                    <PickerPanel
                      key="custom"
                      initialColor={liveColor}
                      onColorChange={handleCustomColor}
                      showPresets={false}
                      liveUpdate
                    />
                  </div>
                </div>
              )}

              {/* Reset — always at bottom */}
              <div className="border-border mt-auto border-t px-5 pb-4 pt-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-lg transition-colors"
                >
                  <RotateCcw className="h-3 w-3" strokeWidth={1.5} />
                  Reset to default
                </button>
              </div>
            </div>

            {/* ── Right — Live Preview ─────────────────────────── */}
            <div className="bg-muted/30 flex flex-1 flex-col gap-4 p-5">
              <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                Preview
              </p>

              <MiniRailPreview railColor={liveColor} />

              <div className="mt-auto space-y-3">
                <div className="flex items-center gap-2.5">
                  <div
                    className="h-8 w-8 shrink-0 rounded-lg border border-black/10 shadow-sm"
                    style={{ backgroundColor: liveColor }}
                  />
                  <div>
                    <p className="text-foreground font-mono text-lg font-medium leading-none">
                      {liveColor.toUpperCase()}
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs">Rail background</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {(() => {
                    const d = deriveSidebarTheme(liveColor);
                    return [
                      { label: 'Icon color', color: d.iconColor },
                      { label: 'Hover bg', color: d.hoverBg },
                      { label: 'Active bg', color: d.activeBg },
                      { label: 'Border', color: d.borderColor },
                    ].map(({ label, color }) => (
                      <div
                        key={label}
                        className="border-border flex items-center gap-1.5 rounded-md border bg-white px-2 py-1.5"
                      >
                        <div
                          className="h-4 w-4 shrink-0 rounded border border-black/10"
                          style={{ backgroundColor: color }}
                        />
                        <div>
                          <p className="text-muted-foreground text-xs leading-none">{label}</p>
                          <p className="text-foreground/60 mt-0.5 font-mono text-xxs">
                            {color.toUpperCase()}
                          </p>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
